import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import HeadingSkeleton from "../../component/HeadingSkeleton";
import { API_BASE_URL } from "../../utils/app_url";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { NotificationManager } from "react-notifications";

const DEFAULT_FORM = {
  company_name: "",
  name: "",
  email: "",
  phone: "",
  message: "",
};

const FairTradeContactUs = () => {
  const { user } = useAuth();
  const { selectedLanguage, langJson } = useLanguage();
  const { storeId } = useParams();

  const [contractData, setContractData] = useState();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [sending, setSending] = useState(false);

  // WhatsApp logic
  const whatsappText = useMemo(() => {
    const lines = [
      `Company: ${form.company_name || "-"}`,
      `Name: ${form.name || "-"}`,
      `Email: ${form.email || "-"}`,
      `Phone: ${form.phone || "-"}`,
      "Message:",
      form.message || "-",
    ];
    return lines.join("\n");
  }, [form]);

  // Use contractData.twitter as WhatsApp value (as in contact-us.js)
  const whatsappConfigValue = useMemo(() => {
    if (contractData?.twitter) return contractData.twitter;
    return "";
  }, [contractData]);

  const whatsappUrl = useMemo(() => {
    const value = (whatsappConfigValue || "").trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    if (/^\d{6,}$/i.test(value)) return `https://wa.me/${value}`;
    if (/^[A-Za-z0-9]{10,}$/i.test(value)) return `https://api.whatsapp.com/message/${value}`;
    return "";
  }, [whatsappConfigValue]);

  const onWhatsappClick = () => {
    if (!whatsappUrl) {
      return;
    }
    const separator = whatsappUrl.includes("?") ? "&" : "?";
    const url = `${whatsappUrl}${separator}text=${encodeURIComponent(whatsappText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}frontend/store/contact-us`, {
        method: "POST",
        body: JSON.stringify({
          store_id: storeId,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });
      const responseData = await response.json();
      if (responseData.res === true) {
        setContractData(responseData.data);
      } else {
        NotificationManager.warning(responseData?.msg);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onFieldChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateForm = () => {
    if (!form.company_name.trim()) {
      NotificationManager.warning(langJson?.companyisrequired || "Company is required");
      return false;
    }
    if (!form.name.trim()) {
      NotificationManager.warning(langJson?.fullnameisrequired || "Name is required");
      return false;
    }
    if (!form.email.trim()) {
      NotificationManager.warning(langJson?.emailisrequired || "Email is required");
      return false;
    }
    if (!isValidEmail(form.email.trim())) {
      NotificationManager.warning(langJson?.emailisnotvalid || "Email is not valid");
      return false;
    }
    if (!form.phone.trim()) {
      NotificationManager.warning(langJson?.phoneisrequired || "Phone is required");
      return false;
    }
    if (!form.message.trim()) {
      NotificationManager.warning(langJson?.messageisrequired || "Message is required");
      return false;
    }
    return true;
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (!validateForm() || sending) {
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}frontend/contact-us`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
        body: JSON.stringify({
          company_name: form.company_name.trim(),
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          store_id: storeId,
          lang: selectedLanguage,
        }),
      });

      const responseData = await response.json();
      if (response.ok && responseData?.res) {
        NotificationManager.success(responseData?.msg || "Message sent successfully");
        setForm(DEFAULT_FORM);
      } else {
        NotificationManager.warning(responseData?.msg || "Failed to send message");
      }
    } catch (error) {
      NotificationManager.error("Something went wrong");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  return (
    <div className="fullContainer">
      <div className="categoryDiv productSliderMain topNew tradePrdts tradeContact">
        <div className="featureDiv productListMain">
          {loading ? (
            <>
              <HeadingSkeleton width="200px" height="28px" />
              <div style={{ marginTop: "20px" }}>
                <div className="skeleton" style={{ height: "20px", width: "30%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "15%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "18%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "22%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "16%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "25%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "30%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "30%", marginBottom: "12px" }} />
                <div className="skeleton" style={{ height: "20px", width: "30%", marginBottom: "12px" }} />
              </div>
            </>
          ) : (
            <>
              <h2 className="storeTabHdn">{langJson?.contactus}</h2>
              <div className="trade-contact-flex">
                <div className="tradeCnt">
                  <p>
                    {langJson?.emailaddress}: <a href={`mailto:${contractData?.email}`}>{contractData?.email}</a> <br />
                    {langJson?.address}: {contractData?.address} <br />
                    {langJson?.zipcode}: {contractData?.zip_code} <br />
                    {langJson?.city}: {contractData?.city} <br />
                    {langJson?.country}: {contractData?.country} <br />
                    {langJson?.state}: {contractData?.state} <br />
                    {langJson?.phone}: {contractData?.phone} <br />
                    {langJson?.website}: <a href={`${contractData?.linkedin}`} target="_blank">{contractData?.linkedin}</a> <br />
                    Facebook: <a href={`${contractData?.facebook}`} target="_blank">{contractData?.facebook}</a> <br />
                    Instagram: <a href={`${contractData?.instagram}`} target="_blank">{contractData?.instagram}</a> <br />
                  </p>
                  <p style={{ whiteSpace: "pre-wrap" }}>{contractData?.contact_address}</p>
                </div>
                <div className="registerDiv">
                  <p>{langJson?.getintouchwithus || "Get in touch with us"}</p>
                  <form onSubmit={onSubmit}>
                    <ul>
                      <li>
                        <input
                          type="text"
                          name="company_name"
                          placeholder={langJson?.company || "Company name"}
                          value={form.company_name}
                          onChange={onFieldChange}
                        />
                      </li>
                      <li>
                        <input
                          type="text"
                          name="name"
                          placeholder={langJson?.fullname || "Name"}
                          value={form.name}
                          onChange={onFieldChange}
                        />
                      </li>
                      <li>
                        <input
                          type="text"
                          name="email"
                          placeholder={langJson?.emailaddress || "Email address"}
                          value={form.email}
                          onChange={onFieldChange}
                        />
                      </li>
                      <li>
                        <input
                          type="text"
                          name="phone"
                          placeholder={langJson?.phonefaxmobilenumber || "Phone/Fax/Mobile number"}
                          value={form.phone}
                          onChange={onFieldChange}
                        />
                      </li>
                      <li className="fullWidth">
                        <textarea
                          name="message"
                          placeholder={langJson?.message || "Message"}
                          value={form.message}
                          onChange={onFieldChange}
                          rows="5"
                        />
                      </li>
                      <li>
                        <button type="submit" disabled={sending}>
                          {sending ? (langJson?.pleasewait || "Please wait...") : (langJson?.send || "Send")}
                        </button>
                        {whatsappUrl ? (
                          <button className="whatsAppBtn" type="button" onClick={onWhatsappClick} style={{ marginLeft: 8 }}>
                            Whatsapp
                          </button>
                        ) : null}
                      </li>
                    </ul>
                  </form>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FairTradeContactUs;
